import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { quoteCart } from '../lib/shopPricing';
import { track } from '../lib/shopApi';

// Cart lives in localStorage so a shopper can leave and come back. Shape:
// { items: [{ lineId, variantId, qty, backsetMm }], install: {selected, doorMaterial,
//   existingLock, sliding, suburb} | null, shippingChoice: 'standard'|'rural',
//   payment: 'card'|'poli', contact: {...} }
const STORAGE_KEY = 'sd_cart_v1';
const EMPTY = { items: [], install: null, shippingChoice: 'standard', payment: 'card', contact: {} };

const CartContext = createContext(null);

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return { ...EMPTY, ...parsed, items: Array.isArray(parsed.items) ? parsed.items : [] };
  } catch {
    return EMPTY;
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* storage unavailable — cart still works for the session */
    }
  }, [cart]);

  const addItem = useCallback((variantId, { qty = 1, backsetMm = null, backsetMeasuredMm = null, noMortice = false, product } = {}) => {
    setCart((c) => {
      const existing = c.items.find((i) => i.variantId === variantId && (i.backsetMm || null) === (backsetMm || null) && !!i.noMortice === !!noMortice);
      const items = existing
        ? c.items.map((i) => (i === existing ? { ...i, qty: Math.min(10, i.qty + qty) } : i))
        : [...c.items, { lineId: `${variantId}-${backsetMm || 'na'}-${noMortice ? 'lev' : 'set'}-${Date.now()}`, variantId, qty, backsetMm, backsetMeasuredMm, noMortice }];
      return { ...c, items };
    });
    track('add_to_cart', { variantId, qty, backsetMm, product: product && product.slug });
  }, []);

  const updateQty = useCallback((lineId, qty) => {
    setCart((c) => ({
      ...c,
      items: c.items.map((i) => (i.lineId === lineId ? { ...i, qty: Math.max(1, Math.min(10, qty)) } : i)),
    }));
  }, []);

  const removeItem = useCallback((lineId) => {
    setCart((c) => ({ ...c, items: c.items.filter((i) => i.lineId !== lineId) }));
  }, []);

  const setInstall = useCallback((install) => setCart((c) => ({ ...c, install })), []);
  const setShippingChoice = useCallback((shippingChoice) => setCart((c) => ({ ...c, shippingChoice })), []);
  const setPayment = useCallback((payment) => setCart((c) => ({ ...c, payment })), []);
  const setContact = useCallback((contact) => setCart((c) => ({ ...c, contact: { ...c.contact, ...contact } })), []);
  const clear = useCallback(() => setCart(EMPTY), []);

  const quote = useMemo(() => (cart.items.length ? quoteCart(cart) : null), [cart]);
  const count = useMemo(() => cart.items.reduce((n, i) => n + (i.qty || 1), 0), [cart.items]);

  const value = useMemo(
    () => ({ cart, quote, count, addItem, updateQty, removeItem, setInstall, setShippingChoice, setPayment, setContact, clear }),
    [cart, quote, count, addItem, updateQty, removeItem, setInstall, setShippingChoice, setPayment, setContact, clear]
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
